import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  ArrowRight,
  Clock,
  Users,
  FileText,
  Eye,
  MessageSquare,
  Briefcase,
  PenTool,
  Globe,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';

interface StatCard {
  label: string;
  value: string | number;
  change?: { value: number; trend: 'up' | 'down' };
  icon: React.ReactNode;
  color: string;
  href?: string;
}

interface QuickAction {
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  color: string;
}

interface ActivityItem {
  id: string;
  message: string;
  type: 'success' | 'warning' | 'info' | 'error';
  timestamp: Date;
}

// Demo data
const demoStats: StatCard[] = [
  {
    label: 'Toplam Kullanici',
    value: 1247,
    change: { value: 12, trend: 'up' },
    icon: <Users size={24} />,
    color: 'text-blue-500',
    href: '/admin/users'
  },
  {
    label: 'Blog Yazilari',
    value: 48,
    change: { value: 8, trend: 'up' },
    icon: <PenTool size={24} />,
    color: 'text-purple-500',
    href: '/admin/blog'
  },
  {
    label: 'Proje Talepleri',
    value: 23,
    change: { value: 5, trend: 'up' },
    icon: <Briefcase size={24} />,
    color: 'text-green-500',
    href: '/admin/projects'
  },
  {
    label: 'Iletisim Mesajlari',
    value: 156,
    icon: <MessageSquare size={24} />,
    color: 'text-orange-500',
    href: '/admin/messages'
  },
  {
    label: 'Sayfa Goruntulenme',
    value: '45.2K',
    change: { value: 18, trend: 'up' },
    icon: <Eye size={24} />,
    color: 'text-cyan-500',
    href: '/admin/analytics'
  },
  {
    label: 'Aktif Kullanici',
    value: 89,
    icon: <Activity size={24} />,
    color: 'text-emerald-500',
    href: '/admin/users'
  },
  {
    label: 'Bekleyen Talepler',
    value: 7,
    icon: <AlertTriangle size={24} />,
    color: 'text-yellow-500',
    href: '/admin/projects'
  },
  {
    label: 'Yayindaki Icerik',
    value: 124,
    icon: <FileText size={24} />,
    color: 'text-accent',
    href: '/admin/content'
  },
];

const demoActivity: ActivityItem[] = [
  { id: '1', message: 'Yeni kullanici kaydi: Ahmet Yilmaz', type: 'success', timestamp: new Date(Date.now() - 5 * 60 * 1000) },
  { id: '2', message: 'Yeni proje talebi alindi: E-ticaret Sitesi', type: 'info', timestamp: new Date(Date.now() - 15 * 60 * 1000) },
  { id: '3', message: 'Blog yazisi yayinlandi: AI Trendleri 2024', type: 'success', timestamp: new Date(Date.now() - 30 * 60 * 1000) },
  { id: '4', message: 'Iletisim formu gonderildi', type: 'info', timestamp: new Date(Date.now() - 45 * 60 * 1000) },
  { id: '5', message: 'Sistem guncelleme tamamlandi', type: 'success', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) },
  { id: '6', message: 'Bekleyen proje talebi: Mobil Uygulama', type: 'warning', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000) },
];

const quickActions: QuickAction[] = [
  {
    label: 'Yeni Blog Yazisi',
    description: 'Blog icerigi olustur',
    href: '/admin/blog',
    icon: <PenTool size={20} />,
    color: 'from-purple-500 to-pink-500'
  },
  {
    label: 'Proje Taleplerini Gor',
    description: 'Bekleyen talepleri incele',
    href: '/admin/projects',
    icon: <Briefcase size={20} />,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    label: 'Mesajlari Oku',
    description: 'Iletisim mesajlarini gor',
    href: '/admin/messages',
    icon: <Mail size={20} />,
    color: 'from-orange-500 to-amber-500'
  },
  {
    label: 'Siteyi Gor',
    description: 'Web sitesine git',
    href: '/',
    icon: <Globe size={20} />,
    color: 'from-green-500 to-emerald-500'
  },
];

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Az once';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} dakika once`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} saat once`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} gun once`;
  }

  return date.toLocaleDateString('tr-TR');
}

export function AdminDashboard() {
  const { session } = useAdminAuth();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Gunaydin';
    if (hour < 18) return 'Iyi gunler';
    return 'Iyi aksamlar';
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-accent/10 to-purple-500/10 rounded-2xl p-6 border border-accent/20">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              {getGreeting()}, {session?.user.name || 'Admin'}!
            </h1>
            <p className="text-text-secondary">
              Bosphorus Labs Admin Paneline hosgeldiniz. Iste bugunun ozeti.
            </p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-sm text-text-tertiary">
              {new Date().toLocaleDateString('tr-TR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <p className="text-xs text-text-tertiary mt-1">
              Son giris: {session?.user.lastLogin ? new Date(session.user.lastLogin).toLocaleString('tr-TR') : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {demoStats.map((stat, index) => (
          <Link
            key={index}
            to={stat.href || '#'}
            className="bg-bg-card border border-border-default rounded-xl p-5 hover:border-accent/50 transition-colors group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={cn('p-2 rounded-lg bg-bg-elevated', stat.color)}>
                {stat.icon}
              </div>
              {stat.change && (
                <div className={cn(
                  'flex items-center gap-1 text-xs px-2 py-1 rounded-full',
                  stat.change.trend === 'up' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                )}>
                  <TrendingUp size={12} />
                  %{stat.change.value}
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-text-primary mb-1">{stat.value}</p>
            <p className="text-sm text-text-secondary group-hover:text-accent transition-colors">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Hizli Islemler</h2>
          <div className="space-y-3">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.href}
                className="flex items-center gap-4 p-4 bg-bg-card border border-border-default rounded-xl hover:border-accent/50 transition-colors group"
              >
                <div className={cn('p-2 rounded-lg bg-gradient-to-br text-white', action.color)}>
                  {action.icon}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary group-hover:text-accent transition-colors">
                    {action.label}
                  </p>
                  <p className="text-xs text-text-tertiary">{action.description}</p>
                </div>
                <ArrowRight size={16} className="text-text-tertiary group-hover:text-accent transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Son Aktiviteler</h2>
          <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
            <div className="divide-y divide-border-default">
              {demoActivity.map((activity) => (
                <div key={activity.id} className="flex items-center gap-4 p-4">
                  <div className={cn(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    activity.type === 'success' && 'bg-green-500',
                    activity.type === 'warning' && 'bg-yellow-500',
                    activity.type === 'info' && 'bg-blue-500',
                    activity.type === 'error' && 'bg-red-500'
                  )} />
                  <p className="flex-1 text-sm text-text-primary">{activity.message}</p>
                  <div className="flex items-center gap-1 text-xs text-text-tertiary">
                    <Clock size={12} />
                    {formatRelativeTime(activity.timestamp)}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 bg-bg-elevated border-t border-border-default">
              <Link to="/admin/activity" className="w-full text-center text-sm text-accent hover:underline block">
                Tum Aktiviteleri Gor
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Sistem Durumu</h2>
          <Link to="/admin/security" className="text-sm text-accent hover:underline flex items-center gap-1">
            Detaylari Gor <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Web Sitesi', status: 'Aktif' },
            { name: 'API', status: 'Aktif' },
            { name: 'Veritabani', status: 'Aktif' },
            { name: 'E-posta', status: 'Aktif' },
          ].map(service => (
            <div key={service.name} className="text-center p-4 rounded-lg bg-bg-elevated">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 bg-green-500/10">
                <CheckCircle2 className="text-green-500" size={20} />
              </div>
              <p className="text-sm text-text-primary font-medium">{service.name}</p>
              <p className="text-xs text-green-500">{service.status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
